// Die Kisten Bestellung, Termin und Montage (E3c, S3 und S4; G22: „Bei
// Termin … ist beim Draufklicken noch nichts da“) – reine Logik, ohne React:
// was eine Zeile über eine Anlage sagt, und wie die Montage nach Tagen
// geordnet wird („welche Partie wann wo“). Seit S5 dazu die Kiste
// Dachplanung: was eine Zeile über Plan und Liste sagt.

import {
  BESTELL_TEIL_NAMEN,
  type BestellKisteZeile,
  type DachplanungKisteZeile,
  type EinsatzArt,
  type MontagePartie,
  type MontageZeile,
  type SchrittStand,
} from "@happy-energy/kern";
import { tagKurz, uhrzeitText } from "../ablauf/ablauf.ts";
import { tagZeigen } from "../anzeige.ts";

/**
 * Die Kiste Dachplanung (seit S5): was an der Anlage von K2 liegt, in einem
 * Satz – „Besichtigung gesendet am 2. Nov. · draußen übersprungen“.
 */
export function dachplanungSatz(z: Pick<DachplanungKisteZeile, "gesendetAm" | "k2">): string {
  const gesendet = `Besichtigung gesendet am ${tagZeigen(z.gesendetAm)}`;
  if (z.k2.pdf) return `${gesendet} · der Plan ist da`;
  if (z.k2.excel) return `${gesendet} · die Liste ist da, der Plan fehlt`;
  return z.k2.uebersprungen ? `${gesendet} · draußen übersprungen` : gesendet;
}

/** Je Teil ein kurzer Satz: „Module → Gautzsch, Lieferung Fr., 4. Dez.“ – „Unterkonstruktion: noch kein Lieferant“. */
export function teilSaetze(z: Pick<BestellKisteZeile, "teile">): string[] {
  return z.teile.map((t) => {
    const name = BESTELL_TEIL_NAMEN[t.teil];
    if (!t.lieferant) return t.erledigtAm ? `${name}: schon erledigt` : `${name}: noch kein Lieferant`;
    const lieferung = t.wunschliefertermin ? `, Lieferung ${tagKurz(t.wunschliefertermin)}` : "";
    return `${name} → ${t.lieferant}${lieferung}${t.erledigtAm ? " – bestellt" : ""}`;
  });
}

/** Eine Partie in einer Zeile: „Dachbau Sonnseite · Mo., 7. Dez., 7:30 Uhr“ – ohne Partie „noch keine Partie“. */
export function partieSatz(p: MontagePartie): string {
  if (!p.name) return "noch keine Partie";
  const wann = p.tag ? ` · ${tagKurz(p.tag)}${p.uhrzeit ? `, ${uhrzeitText(p.uhrzeit)}` : ""}` : "";
  return `${p.name}${wann}`;
}

/** Ob eine Partie abgehakt ist (beauftragt, eingeteilt, schon erledigt). */
export function partieErledigt(stand: SchrittStand): boolean {
  return stand === "erledigt" || stand === "vor-dem-update";
}

/** Ein Einsatz an einem Tag der Montage. */
export interface MontageEintrag {
  zeile: MontageZeile;
  art: EinsatzArt;
  /** Die Partie – null: An diesem Tag ist Montage, aber für diese Art ist noch keine Partie gewählt. */
  partie: string | null;
  uhrzeit: string | null;
  erledigt: boolean;
  /** Seit S7: An diesem Tag fehlen Dach- UND Bodenpartie – eine Zeile „noch keine Partie“ statt zwei. */
  beide?: boolean;
}

export interface MontageTag {
  tag: string;
  eintraege: MontageEintrag[];
}

/**
 * Die Montage nach Tagen (Kiste Montage): je kommendem Tag, welche Partie wo
 * ist – der Tag der Partie, nicht der des Termins (eine Bodenpartie kann am
 * Tag danach kommen). Eine Anlage, deren Termin kommt, die aber für eine Art
 * noch keine Partie mit Tag hat, steht an ihrem Termin mit „noch keine
 * Dachpartie“ bzw. „noch keine Bodenpartie“ – fehlen beide am selben Tag,
 * steht sie dort einmal mit „noch keine Partie“ (seit S7). Je Tag zuerst nach
 * Uhrzeit (ohne zuletzt), dann Dach vor Boden, dann nach Kunde.
 */
export function montageTage(zeilen: readonly MontageZeile[], heute: string): MontageTag[] {
  const tage = new Map<string, MontageEintrag[]>();
  const dazu = (tag: string, e: MontageEintrag) => {
    if (tag < heute) return;
    const liste = tage.get(tag) ?? [];
    liste.push(e);
    tage.set(tag, liste);
  };
  for (const z of zeilen) {
    for (const art of ["dach", "boden"] as const) {
      const p = z[art];
      if (p.name && p.tag) dazu(p.tag, { zeile: z, art, partie: p.name, uhrzeit: p.uhrzeit, erledigt: partieErledigt(p.stand) });
      else if (!partieErledigt(p.stand) && z.montagetermin.am) dazu(z.montagetermin.am, { zeile: z, art, partie: null, uhrzeit: null, erledigt: false });
    }
  }
  const nachName = new Intl.Collator("de", { sensitivity: "base", numeric: true });
  const artRang = (a: EinsatzArt) => (a === "dach" ? 0 : 1);
  const zusammen = (eintraege: MontageEintrag[]): MontageEintrag[] => {
    const ohne = (e: MontageEintrag) => e.partie === null;
    return eintraege.flatMap((e) => {
      if (!ohne(e)) return [e];
      const zwilling = eintraege.find((x) => x !== e && ohne(x) && x.zeile.anlageId === e.zeile.anlageId);
      if (!zwilling) return [e];
      return e.art === "dach" ? [{ ...e, beide: true }] : [];
    });
  };
  return [...tage.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([tag, eintraege]) => ({
      tag,
      eintraege: zusammen(eintraege).sort(
        (x, y) =>
          (x.uhrzeit ?? "99:99").localeCompare(y.uhrzeit ?? "99:99") || artRang(x.art) - artRang(y.art) || nachName.compare(x.zeile.kundeName, y.zeile.kundeName),
      ),
    }));
}
